// No direct server time import in shared; allow injection or fallback to Date.now

/**
 * No-op base class for mixin pattern.
 * Provides a base class for the mixin factory to extend from.
 */
class NoOpBase {}

/**
 * Factory function to create a shared Action class that extends a base class.
 * This enables the mixin pattern for environment-specific Action implementations.
 * @param {class} BaseClass - The base class to extend (e.g., NoOpBase or environment-specific Action)
 * @returns {class} A class that extends BaseClass with shared Action functionality
 */
export function createSharedAction(BaseClass) {
    return class SharedAction extends BaseClass {
        /**
         * Constructs an Action instance.
         * PASSIVE PATTERN: Check for injected service BEFORE super(), only create as fallback.
         * @param {object} parameters - The parameters for the action.
         */
        constructor(parameters = {}) {
            // PASSIVE: Check for injected service first (from environment constructor)
            if (parameters.service) {
                super(parameters);
                this.service = parameters.service;
            } else {
                // Fallback: create service if none provided (shared-only usage)
                const ActionServiceClass = parameters?.ActionService;
                let service;
                if (ActionServiceClass) {
                    service = new ActionServiceClass(parameters);
                } else {
                    service = new (createSharedActionService(Object))(parameters);
                }
                // Remove ActionService class before calling super (Rule 4)
                const { ActionService: _, ...paramsWithoutClass } = parameters;
                super({ ...paramsWithoutClass, service });
                this.service = service;
            }
        }

    // Getters and setters for action properties, delegated to ActionService.
    get type() { return this.service.type; }
    set type(type) { this.service.type = type; }
    get status() { return this.service.status; }
    set status(status) { this.service.status = status; }
    get source() { return this.service.source; }
    set source(source) { this.service.source = source; }
    get target() { return this.service.target; }
    set target(target) { this.service.target = target; }
    get updatedOn() { return this.service.updatedOn; }
    set updatedOn(updatedOn) { this.service.updatedOn = updatedOn; }
    get error() { return this.service.model?.error; }
    set error(error) { if (this.service.model) this.service.model.error = error; }
    get payload() { return this.service.payload; }
    set payload(payload) { this.service.payload = payload; }
    get operation() { return this.service.operation; }
    set operation(operation) { this.service.operation = operation; }
    get queueMode() { return this.service.queueMode; }
    set queueMode(queueMode) { this.service.queueMode = queueMode; }
    
    /**
     * Unique identifier for this action instance (UUID).
     * Used for tracking actions through the client-server lifecycle.
     * Assigned by ActionQueue when the action is enqueued.
     * @type {string|undefined}
     */
    get actionId() { return this._actionId; }
    set actionId(id) { this._actionId = id; }

    /**
     * Requests the action to be processed.
     * @returns {void}
     */
    request() {
        return this.service.request();
    }

    /**
     * Marks the action as received.
     * @returns {Promise<void>|void}
     */
    async receive() {
        await this.service.receive();
    }

    /**
     * Updates the action.
     * @returns {Promise<void>|void}
     */
    async update() {
        await this.service.update();
    }

    /**
     * Completes the action.
     * @returns {Promise<void>|void}
     */
    async complete() {
        await this.service.complete();
    }

    /**
     * Cancels the action.
     * @returns {void}
     */
    cancel() {
        return this.service.cancel();
    }

    /**
     * Architecture_Networking: Applies a server-originated action that arrives already completed.
     * Used for the "completed-only pattern" where server-initiated actions skip
     * lifecycle transitions and are applied directly to client state.
     * 
     * Subclasses should override this method to implement action-specific
     * application logic (e.g., updating UI, applying state changes).
     * 
     * @param {Object} ctx - Context with resolvers and game refs.
     * @param {Object} [ctx.isekaiGame] - Game instance for UI updates.
     * @param {Object} [ctx.gameObjects] - GameObjects controller.
     * @returns {void}
     */
    applyCompleted(ctx = {}) {
        // Base implementation does nothing - subclasses override for specific behavior
        // This is intentionally a no-op since different action types need different logic
    }

    /**
     * Resolve the default world interaction position for target-backed actions.
     * Actions opt into adjacency/on-top movement by calling this from their
     * getRequiredAdjacentPosition() or getRequiredOnTopPosition() override.
     * @returns {{x:number,y:number}|null} Target position or null when unavailable.
     */
    getInteractionTargetPosition() {
        return this.service.getInteractionTargetPosition?.() ?? null;
    }

    /**
     * Marks the action as failed with error status and stores error details.
     * @param {Object|string} error - Error details (object with message, code, context) or error message string
     */
    fail(error) {
        this.status = 'error';
        if (typeof error === 'string') {
            this.service.model.error = { message: error };
        } else if (error && typeof error === 'object') {
            this.service.model.error = error;
        } else {
            this.service.model.error = { message: 'Action failed' };
        }
    }

        /**
         * Processes the action using the service.
         * @returns {void}
         */
        async process() { await this.service.process({action:this}); }
    };
}

/**
 * Factory function to create a shared ActionService class that extends a base class.
 * @param {class} BaseServiceClass - The base service class to extend
 * @returns {class} A class that extends BaseServiceClass with shared ActionService functionality
 */
export function createSharedActionService(BaseServiceClass) {
    return class SharedActionService extends BaseServiceClass {
        /**
         * Constructs an ActionService instance.
         * PASSIVE PATTERN: Check for injected model BEFORE super(), only create as fallback.
         * @param {object} parameters - The parameters for the action.
         */
        constructor(parameters = {}) {
            // PASSIVE: Check for injected model first (from environment constructor)
            if (parameters.model) {
                super(parameters);
                this.model = parameters.model;
            } else {
                // Fallback: create model if none provided (shared-only usage)
                const ActionModelClass = parameters?.ActionModel;
                let model;
                if (ActionModelClass) {
                    model = new ActionModelClass(parameters);
                } else {
                    model = new (createSharedActionModel(Object))(parameters);
                }
                // Remove ActionModel class before calling super (Rule 4)
                const { ActionModel: _, ...paramsWithoutClass } = parameters;
                super({ ...paramsWithoutClass, model });
                this.model = model;
            }
            this.timeProvider = parameters?.timeProvider || (() => (typeof Date.now === 'function' ? Math.floor(Date.now()/1000) : 0));
        }

    // Getters and setters for action properties, delegated to ActionModel.
    get type() { return this.model.type; }
    set type(type) { this.model.type = type; }
    get status() { return this.model.status; }
    set status(status) { this.model.status = status; }
    get source() { return this.model.source; }
    set source(source) { this.model.source = source; }
    get target() { return this.model.target; }
    set target(target) { this.model.target = target; }
    get updatedOn() { return this.model.updatedOn; }
    set updatedOn(updatedOn) { this.model.updatedOn = updatedOn; }
    get error() { return this.model.error; }
    set error(error) { this.model.error = error; }
    get payload() { return this.model.payload; }
    set payload(payload) { this.model.payload = payload; }
    get operation() { return this.model.operation; }
    set operation(operation) { this.model.operation = operation; }
    get queueMode() { return this.model.queueMode; }
    set queueMode(queueMode) { this.model.queueMode = queueMode; }
    
    // actionId for response matching
    get actionId() { return this.model.actionId; }
    set actionId(actionId) { this.model.actionId = actionId; }

    /**
     * Marks the action as requested and updates the timestamp.
     * @returns {void}
     */
    request() {
        this.status = 'requested';
        this.updateTimestamp();
    }

    /**
     * Marks the action as received and updates the timestamp.
     * @returns {void}
     */
    receive() {
        this.status = 'received';
        this.updateTimestamp();
    }

    /**
     * Marks the action as updated and updates the timestamp.
     * @returns {Promise<void>}
     */
    async update() {
        this.status = 'updated';
        this.updateTimestamp();
    }

    /**
     * Marks the action as completed and updates the timestamp.
     * @returns {void}
     */
    complete() {
        this.status = 'completed';
        this.updateTimestamp();
    }

    /**
     * Marks the action as cancelled and updates the timestamp.
     * Called when user or system cancels an in-progress action.
     * @returns {void}
     */
    cancel() {
        this.status = 'cancelled';
        this.updateTimestamp();
    }

    /**
     * Processes the action based on its current status.
     * @returns {void}
     */
    process() {
        switch (this.status) {
            case 'requested':
                this.request();
                break;
            case 'received':
                this.receive();
                break;
            case 'updated':
                this.update();
                break;
            case 'completed':
                this.complete();
                break;
        }
    }

        /**
         * Updates the timestamp for the action.
         * @returns {void}
         */
        updateTimestamp() {
            // Epoch seconds expected across project
            this.updatedOn = this.timeProvider();
        }

        /**
         * Serialize action for public broadcast to observers.
         * Returns a redacted payload safe for non-participant observers.
         * 
         * Default implementation returns minimal action metadata without sensitive details.
         * Subclasses can override to:
         * - Return same as toEmit() for fully public actions (e.g., MoveAction)
         * - Return partial data hiding sensitive fields (e.g., CutAction hides tool durability)
         * - Return null to prevent broadcast entirely (e.g., administrative actions)
         * 
         * @returns {Object|null} Public emission payload, or null to suppress broadcast.
         */
        toPublicEmit() {
            // Default: minimal metadata without sensitive payload details
            return {
                type: this.type,
                status: this.status,
                sourceId: this.source?.objectId ?? null,
                targetId: this.target?.objectId ?? null,
                updatedOn: this.updatedOn
            };
        }

        /**
         * Resolve the default target-backed interaction position.
         * @returns {{x:number,y:number}|null} Target position or null when unavailable.
         */
        getInteractionTargetPosition() {
            const position = this.target?.position;
            if (position && typeof position.x === 'number' && typeof position.y === 'number') {
                return { x: position.x, y: position.y };
            }
            return null;
        }
    };
}

/**
 * Factory function to create a shared ActionModel class that extends a base class.
 * @param {class} BaseModelClass - The base model class to extend
 * @returns {class} A class that extends BaseModelClass with shared ActionModel functionality
 */
export function createSharedActionModel(BaseModelClass) {
    return class SharedActionModel extends BaseModelClass {
        /**
         * The type of action, which also corresponds to the child class (e.g., 'move', 'cut').
         * @type {string|null}
         */
        type = null;
        /**
         * The status of the action, set by the ActionService. Can be 'requested', 'received', 'updated', or 'completed'.
         * @type {string|null}
         */
        status = null;
        /**
         * The source GameObject that initiated the action.
         * @type {object|null}
         */
        source = null;
        /**
         * The target GameObject that is the target of the action.
         * @type {object|null}
         */
        target = null;
        /**
         * The server time (timestamp) when the action was last updated.
         * @type {number}
         */
        updatedOn = 0;
        /**
         * Error details if action failed.
         * @type {Object|null}
         */
        error = null;
        /**
         * Action-specific payload data (Architecture_Networking).
         * Contains all action-specific fields separate from routing/lifecycle properties.
         * @type {Object|null}
         */
        payload = null;
        /**
         * Operation subtype for composite actions (e.g., SyncAction, WorldAction).
         * @type {string|null}
         */
        operation = null;
        /**
         * Queue mode flag. When true, action is queued behind current action.
         * When false (default), current action is cancelled in favor of new action.
         * Set via shift+click on client.
         * @type {boolean}
         */
        queueMode = false;
        
        /**
         * Unique action identifier for precise response matching.
         * Assigned by client ActionQueue when action is enqueued.
         * Preserved through server processing for response correlation.
         * @type {string|null}
         */
        actionId = null;

        /**
         * Constructs an ActionModel instance.
         * @param {object} parameters - The parameters for the action.
         * @param {string} [parameters.type=null] - The type of action.
         * @param {string} [parameters.status=null] - The status of the action.
         * @param {object} [parameters.source=null] - The source GameObject.
         * @param {object} [parameters.target=null] - The target GameObject.
         * @param {object} [parameters.payload=null] - Action-specific data (Architecture_Networking).
         * @param {string} [parameters.operation=null] - Operation subtype for composite actions.
         */
        constructor(parameters = {}) {
            super(parameters);
            this.type = parameters.type || null;
            this.status = parameters.status || 'requested';
            this.source = parameters.source || null;
            this.target = parameters.target || null;
            this.payload = parameters.payload || null;
            this.operation = parameters.operation || null;
            // Preserve queueMode exactly as provided (true, false, or undefined)
            // - true: queue behind current action (shift+click)
            // - false: interrupt current action (explicit cancel)
            // - undefined: client ActionQueue doesn't send this, treat as queue mode in Player.js
            this.queueMode = parameters.queueMode;
            // Preserve actionId for response matching
            this.actionId = parameters.actionId || null;
            // Default to epoch seconds; ActionService may overwrite on lifecycle transitions
            this.updatedOn = (typeof Date.now === 'function') ? Math.floor(Date.now()/1000) : 0;
        }

        /**
         * Serializes the ActionModel to a plain object for JSON transport.
         * Architecture_Networking: Supports both legacy format and new payload structure.
         * @param {object} [options={}] - Serialization options.
         * @param {boolean} [options.usePayload=false] - If true, use Architecture_Networking payload structure.
         * @returns {object} The JSON representation of the action.
         */
        toJSON(options = {}) {
            const base = {
                type: this.type,
                status: this.status,
                updatedOn: this.updatedOn
            };
            
            // Include actionId for precise response matching
            if (this.actionId) {
                base.actionId = this.actionId;
            }

            // Include queueMode if true (default is false/interrupt)
            if (this.queueMode) {
                base.queueMode = true;
            }

            // Include operation if present (for composite actions like SyncAction)
            if (this.operation) {
                base.operation = this.operation;
            }

            // Include error if present
            if (this.error) {
                base.error = this.error;
            }

            if (options.usePayload) {
                // Architecture_Networking format: action-specific data in payload property
                base.payload = {
                    source: this.source ? { objectId: this.source.objectId } : null,
                    target: this.target ? { objectId: this.target.objectId } : null,
                    ...(this.payload || {})
                };
            } else {
                // Legacy format: source/target at top level
                base.source = this.source ? { objectId: this.source.objectId } : null;
                base.target = this.target ? { objectId: this.target.objectId } : null;
            }

            return base;
        }
    };
}

// Export default instances for backward compatibility with existing code
// Create ActionModel first
const SharedActionModel = createSharedActionModel(Object);

// Create ActionService that uses SharedActionModel by default
class SharedActionService extends createSharedActionService(Object) {
    constructor(parameters = {}) {
        // PASSIVE PATTERN: Only add ActionModel if no model provided
        if (parameters.model) {
            // Model already provided, don't add ActionModel class
            super(parameters);
        } else {
            // No model provided, inject SharedActionModel as default
            super({ ...parameters, ActionModel: parameters?.ActionModel || SharedActionModel });
        }
    }
}

// Create Action class that uses SharedActionService by default
class SharedAction extends createSharedAction(NoOpBase) {
    constructor(parameters = {}) {
        // PASSIVE PATTERN: Only add ActionService if no service provided
        if (parameters.service) {
            // Service already provided, don't add ActionService class
            super(parameters);
        } else {
            // No service provided, inject SharedActionService as default
            super({ ...parameters, ActionService: parameters?.ActionService || SharedActionService });
        }
    }
}

export { SharedAction as Action, SharedActionService as ActionService, SharedActionModel as ActionModel };

